<template>
  <div class="fixed inset-0 bg-gray-800 bg-opacity-50 flex items-center justify-center p-4 animate-modalFade" v-if="!isClosed">
    <div class="w-full max-w-2xl bg-gray-800 text-gray-300 rounded-lg p-4 sm:p-6 flex flex-col gap-4 shadow-lg border border-gray-700 animate-scaleIn">
      <!-- Header with Countdown Timer and Close Button -->
      <div class="flex justify-between items-center">
        <!-- Countdown Timer (Moved to Left) -->
        <div v-if="timeLeft !== null" class="font-semibold text-xxs sm:text-xs bg-gray-800 bg-opacity-90 border border-gray-600 rounded p-1">
          <div class="flex items-center space-x-2 sm:space-x-3 text-center">
            <span class="px-2 py-0.5 bg-gray-600 text-gray-100 rounded">
              {{ mapName || 'ไม่ระบุแผนที่' }}
            </span>
            <span :class="isDanger ? 'text-red-600' : 'text-yellow-700'">
              เหลือเวลา: {{ formatTime(timeLeft) }}
            </span>
            <span class="text-green-700">
              <i class="fas fa-skull mr-1"></i>วิญญาณที่ฆ่า: {{ ghostKilled }}
            </span>
          </div>
          <p v-if="!fightResult && isDanger" class="mt-1 text-xxs text-red-700 text-center">
            <i class="fas fa-exclamation-triangle mr-1"></i>
            คำเตือน: เหลือเวลาไม่ถึง 5 นาที ต้องรีบกลับบ้าน
          </p>
        </div>
        <!-- Close Button with Minus Icon -->
        <button @click="closeModal" class="text-gray-300 hover:text-red-500 focus:outline-none" title="ปิดหน้าต่าง">
          <i class="fas fa-minus text-lg"></i>
        </button>
      </div>

      <!-- ✅ Boss Status Bar — วงกลมบอส เรียงเกิดก่อน ตายหลัง -->
      <div
        v-if="mapBossStatuses && Object.keys(mapBossStatuses).length > 0"
        class="flex flex-wrap gap-1.5 px-1"
      >
        <div
          v-for="(status, bossName) in sortedMapBossStatuses"
          :key="bossName"
          class="relative group/boss flex-shrink-0"
          style="width:36px;height:36px;"
          :style="{ opacity: status.isAlive ? 1 : 0.5 }"
        >
          <!-- SVG circular progress ring -->
          <svg width="36" height="36" viewBox="0 0 36 36" class="absolute inset-0" style="transform:rotate(-90deg);">
            <circle cx="18" cy="18" r="15" fill="#111827" fill-opacity="0.75"/>
            <circle cx="18" cy="18" r="13" fill="none" stroke="#374151" stroke-width="3"/>
            <circle
              cx="18" cy="18" r="13" fill="none"
              :stroke="status.isAlive ? '#22c55e' : '#f97316'"
              stroke-width="3"
              stroke-linecap="round"
              :stroke-dasharray="81.68"
              :stroke-dashoffset="81.68 * (1 - (status.progress ?? 1))"
              style="transition: stroke-dashoffset 1s linear, stroke 0.3s ease;"
            />
          </svg>
          <!-- รูปบอส -->
          <div class="absolute inset-0 flex items-center justify-center" style="padding:4px;">
            <img
              v-if="status.imageUrl"
              :src="status.imageUrl"
              :alt="bossName"
              class="w-full h-full rounded-full object-cover"
              @error="$event.target.style.display='none'"
            />
            <i v-else class="fas fa-skull-crossbones text-gray-400" style="font-size:9px;"></i>
          </div>
          <!-- Tooltip hover -->
          <div class="absolute top-10 left-0 z-20 hidden group-hover/boss:flex flex-col bg-gray-900 text-white text-[10px] px-2 py-1 rounded whitespace-nowrap border border-gray-600 shadow-lg pointer-events-none">
            <span class="font-semibold text-gray-200">{{ bossName }}</span>
            <span :class="status.isAlive ? 'text-green-400' : 'text-orange-400'">
              {{ status.isAlive ? '✅ เกิดแล้ว' : '○ ยังไม่เกิด' }}
            </span>
          </div>
        </div>
      </div>

      <!-- Initial Loading -->
      <div v-if="isLoading" class="flex flex-col items-center justify-center py-4">
        <div class="animate-spin rounded-full h-12 w-12 border-t-4 border-red-600"></div>
        <p class="text-gray-300 text-sm mt-2">กำลังโหลด...</p>
      </div>
      <!-- Opponent Section (Ghost or Merchant) -->
      <div v-if="opponentData && !isLoading" class="flex flex-col items-center gap-3 bg-gray-900 border border-gray-700 rounded-md p-4">
        <div class="flex items-center gap-3 w-full">
          <img
            :src="opponentType === 'merchant' ? (opponentData?.angryFace || opponentData?.img || 'default-merchant.png') : (opponentData?.imageUrl || opponentData?.img || 'default-ghost.png')"
            :alt="opponentType === 'ghost' ? 'Ghost' : 'Merchant'"
            class="w-16 sm:w-20 h-16 sm:h-20 rounded-md object-cover border-2 border-red-600"
            @error="handleImageError"
          />
          <div class="flex-1">
            <h2 class="text-base sm:text-lg font-semibold text-red-600 text-center">
              {{ opponentData?.name || (opponentType === 'ghost' ? 'วิญญาณ' : 'พ่อค้า') }}
              <span v-if="opponentType === 'merchant'">(กำลังปล้น)</span>
            </h2>
            <div class="grid grid-cols-7 gap-2 text-center mt-2 bg-gray-800 bg-opacity-30 p-2 rounded-md">
              <div class="flex flex-col items-center">
                <i class="fas fa-heartbeat text-red-600"></i>
                <p class="text-xxs sm:text-sm font-bold text-gray-100 mt-1">{{ opponentStats.maxHp }}</p>
              </div>
              <div class="flex flex-col items-center">
                <i class="fas fa-fist-raised text-red-600"></i>
                <p class="text-xxs sm:text-sm font-bold text-gray-100 mt-1">{{ opponentStats.atk }}</p>
              </div>
              <div class="flex flex-col items-center">
                <i class="fas fa-running text-red-600"></i>
                <p class="text-xxs sm:text-sm font-bold text-gray-100 mt-1">{{ opponentStats.evasion }}%</p>
              </div>
              <div class="flex flex-col items-center">
                <i class="fas fa-crosshairs text-red-600"></i>
                <p class="text-xxs sm:text-sm font-bold text-gray-100 mt-1">{{ opponentStats.critical }}%</p>
              </div>
              <div class="flex flex-col items-center">
                <i class="fa-solid fa-shield-virus text-red-600"></i>
                <p class="text-xxs sm:text-sm font-bold text-gray-100 mt-1">{{ opponentStats.criticalReduction }}%</p>
              </div>
              <div class="flex flex-col items-center">
                <i class="fas fa-shield-alt text-red-600"></i>
                <p class="text-xxs sm:text-sm font-bold text-gray-100 mt-1">{{ opponentStats.defense }}</p>
              </div>
              <div class="flex flex-col items-center">
                <i class="fa-solid fa-person-rays text-red-600"></i>
                <p class="text-xxs sm:text-sm font-bold text-gray-100 mt-1">{{ opponentStats.damageReduction }}%</p>
              </div>
            </div>
          </div>
        </div>
        <div class="w-full relative">
          <div class="bg-gray-500 h-5 rounded overflow-hidden">
            <div
              class="bg-red-600 h-5 rounded transition-all duration-200"
              :style="{ width: opponentType === 'merchant' ? '100%' : `${(currentOpponentHp / opponentStats.maxHp * 100) || 0}%` }"
            ></div>
            <p class="absolute inset-0 flex items-center justify-center text-xs text-gray-100">
              HP: {{ opponentType === 'merchant' ? '??' : currentOpponentHp }} / {{ opponentType === 'merchant' ? '??' : opponentStats.maxHp }}
            </p>
          </div>
        </div>
      </div>
      <!-- Player Section (with opponent) -->
      <div v-if="opponentData && !isLoading" class="flex flex-col items-center gap-3 bg-gray-900 border border-gray-700 rounded-md p-4">
        <div class="flex items-center gap-3 w-full">
          <div class="flex-1">
            <h2 class="text-base sm:text-lg font-semibold text-blue-600 text-center">{{ characterData?.characterName || 'ผู้เล่น' }}</h2>
            <div class="grid grid-cols-7 gap-2 text-center mt-2 bg-gray-800 bg-opacity-30 p-2 rounded-md">
              <div class="flex flex-col items-center">
                <i class="fas fa-heartbeat text-blue-600"></i>
                <p class="text-xxs sm:text-sm font-bold text-gray-100 mt-1">{{ calculatedStats.maxHp }}</p>
              </div>
              <div class="flex flex-col items-center">
                <i class="fas fa-fist-raised text-blue-600"></i>
                <p class="text-xxs sm:text-sm font-bold text-gray-100 mt-1">{{ calculatedStats.atk }}</p>
              </div>
              <div class="flex flex-col items-center">
                <i class="fas fa-running text-blue-600"></i>
                <p class="text-xxs sm:text-sm font-bold text-gray-100 mt-1">{{ calculatedStats.evasion }}%</p>
              </div>
              <div class="flex flex-col items-center">
                <i class="fas fa-crosshairs text-blue-600"></i>
                <p class="text-xxs sm:text-sm font-bold text-gray-100 mt-1">{{ calculatedStats.critical }}%</p>
              </div>
              <div class="flex flex-col items-center">
                <i class="fa-solid fa-shield-virus text-blue-600"></i>
                <p class="text-xxs sm:text-sm font-bold text-gray-100 mt-1">{{ calculatedStats.criticalReduction }}%</p>
              </div>
              <div class="flex flex-col items-center">
                <i class="fas fa-shield-alt text-blue-600"></i>
                <p class="text-xxs sm:text-sm font-bold text-gray-100 mt-1">{{ calculatedStats.defense }}</p>
              </div>
              <div class="flex flex-col items-center">
                <i class="fa-solid fa-person-rays text-blue-600"></i>
                <p class="text-xxs sm:text-sm font-bold text-gray-100 mt-1">{{ calculatedStats.damageReduction }}%</p>
              </div>
            </div>
          </div>
          <img
            :src="characterData?.imageUrl || 'default-character.png'"
            alt="Character"
            class="w-16 sm:w-20 h-16 sm:h-20 rounded-md object-cover border-2 border-blue-700"
            @error="handleImageError"
          />
        </div>
        <div class="w-full space-y-2">
          <!-- HP Bar -->
          <div class="relative">
            <div class="bg-gray-500 h-5 rounded overflow-hidden">
              <div class="bg-red-600 h-5 rounded transition-all duration-200"
                   :style="{ width: `${(currentHealth / calculatedStats.maxHp * 100) || 0}%` }"></div>
              <p class="absolute inset-0 flex items-center justify-center text-xs text-gray-100">
                HP: {{ currentHealth }} / {{ calculatedStats.maxHp }}
              </p>
            </div>
          </div>
          <!-- Energy Bar -->
          <div class="relative">
            <div class="bg-gray-500 h-5 rounded overflow-hidden">
              <div class="bg-blue-600 h-5 rounded transition-all duration-200"
                   :style="{ width: `${(energyLevel / maxEnergy * 100) || 0}%` }"></div>
              <p class="absolute inset-0 flex items-center justify-center text-xs text-gray-100">
                พลังงาน: {{ energyLevel || 0 }} / {{ maxEnergy }}
              </p>
            </div>
          </div>
        </div>
      </div>
      <!-- Tabs -->
      <div v-if="!opponentData && (hasReward || hasSituation || hasMerchant || hasBossGhost) && !isLoading" class="flex flex-col gap-3">
        <div class="flex border-b border-gray-600">
          <button
            v-if="hasReward"
            @click="activeTab = 'rewards'"
            class="relative px-4 py-2 text-sm font-semibold transition-all duration-200"
            :class="activeTab === 'rewards' ? 'bg-gray-600 text-gray-100 border-b-2 border-indigo-500' : 'text-gray-300 hover:bg-gray-600'">
            รางวัล
          </button>
          <button
            v-if="hasSituation"
            @click="activeTab = 'situations'"
            class="relative px-4 py-2 text-sm font-semibold transition-all duration-200"
            :class="activeTab === 'situations' ? 'bg-gray-600 text-gray-100 border-b-2 border-indigo-500' : 'text-gray-300 hover:bg-gray-600'">
            สถานการณ์
            <span v-if="situations.length > 0"
                  class="absolute top-0 right-0 translate-x-1/2 -translate-y-1/2 bg-indigo-600 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
              {{ situations.length }}
            </span>
          </button>
          <button
            v-if="hasMerchant"
            @click="activeTab = 'merchants'"
            class="relative px-4 py-2 text-sm font-semibold transition-all duration-200"
            :class="activeTab === 'merchants' ? 'bg-gray-600 text-gray-100 border-b-2 border-indigo-500' : 'text-gray-300 hover:bg-gray-600'">
            พ่อค้า
            <span v-if="merchants.length > 0"
                  class="absolute top-0 right-0 translate-x-1/2 -translate-y-1/2 bg-indigo-600 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
              {{ merchants.length }}
            </span>
          </button>
          <button
            v-if="hasBossGhost"
            @click="activeTab = 'bossGhosts'"
            class="relative px-4 py-2 text-sm font-semibold transition-all duration-200"
            :class="activeTab === 'bossGhosts' ? 'bg-gray-600 text-gray-100 border-b-2 border-indigo-500' : 'text-gray-300 hover:bg-gray-600'">
            บอสวิญญาณ
            <span v-if="bossGhosts.length > 0"
                  class="absolute top-0 right-0 translate-x-1/2 -translate-y-1/2 bg-indigo-600 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
              {{ bossGhosts.length }}
            </span>
          </button>
        </div>
        <!-- Rewards Tab -->
        <div v-if="activeTab === 'rewards' && hasReward" class="flex flex-col gap-3">
          <div v-if="isLoadingRewards" class="flex flex-col items-center justify-center py-4">
            <div class="animate-spin rounded-full h-8 w-8 border-t-2 border-indigo-500"></div>
            <p class="text-gray-300 text-xs mt-2">กำลังโหลดรางวัล...</p>
          </div>
          <div v-else class="bg-gray-600 p-2 rounded-md flex items-center justify-between gap-2 border border-gray-600">
            <div class="flex items-center gap-3 flex-wrap">
              <div class="flex items-center gap-2">
                <span class="text-xxs sm:text-xs text-gray-300">น้ำหนัก</span>
              </div>
              <div class="flex items-center gap-2">
                <span class="px-2 py-0.5 rounded bg-gray-700 text-gray-100 text-xxs font-semibold border border-gray-500">
                  {{ selectedWeight }} / {{ capacity }}
                </span>
              </div>
            </div>
            <div class="flex items-center gap-2">
              <button
                @click="toggleAllSelections"
                class="inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-white text-xxs sm:text-xs font-semibold focus:outline-none focus:ring-2"
                :class="selectedRewardIds.length
                  ? 'bg-yellow-600 hover:bg-yellow-700 focus:ring-yellow-700'
                  : 'bg-green-600 hover:bg-green-700 focus:ring-green-700'"
                :disabled="isFighting || isAddingToBag || (!rewards.length && !selectedRewardIds.length)"
                :title="selectedRewardIds.length ? 'ยกเลิกการเลือกทั้งหมด' : 'เลือกทั้งหมดแบบพอดีน้ำหนัก'">
                <i v-if="!selectedRewardIds.length" class="fas fa-check-double text-xs"></i>
                <i v-else class="fas fa-rotate-left text-xs"></i>
                <span class="hidden xs:inline">{{ selectedRewardIds.length ? 'ยกเลิกทั้งหมด' : 'เลือกทั้งหมด' }}</span>
                <span class="inline sm:hidden">{{ selectedRewardIds.length ? 'ยกเลิก' : 'เลือก' }}</span>
              </button>
            </div>
          </div>
          <ul v-if="!isLoadingRewards" class="bg-gray-600 rounded-md p-2 max-h-64 overflow-y-auto scrollbar-thin border border-gray-600">
            <li
              v-for="reward in rewards"
              :key="reward.id"
              @click="toggleRewardSelection(reward.id)"
              class="flex items-center gap-3 p-2 cursor-pointer transition-all duration-200 rounded-md mb-2 last:mb-0 border"
              :class="selectedRewardIds.includes(reward.id)
                ? 'bg-gray-700 border-l-4 border-indigo-500'
                : 'bg-gray-700 bg-opacity-40 hover:bg-opacity-60 border-gray-600'">
              <img
                :src="reward.img || 'default-item.png'"
                alt="Reward"
                class="w-10 h-10 rounded-md object-cover border-2 flex-shrink-0"
                :class="reward.rarityColors?.borderColor"
                @error="handleImageError"
              />
              <div class="flex-1 min-w-0">
                <p class="text-xs font-semibold truncate" :class="reward.rarityColors?.textColor || 'text-gray-100'">
                  {{ reward.name }} <span v-if="reward.amount">x {{ reward.amount }}</span>
                  <i v-if="reward.transfer" class="fas fa-arrow-right-arrow-left text-green-500 text-xxs ml-1"></i>
                </p>
                <p class="text-xxs text-gray-400">น้ำหนัก: {{ reward.weight || 0 }}</p>
              </div>
              <i class="fas" :class="selectedRewardIds.includes(reward.id) ? 'fa-check-circle text-green-500' : 'fa-circle text-gray-500'"></i>
            </li>
            <li v-if="!rewards.length" class="text-center text-gray-300 text-xs py-4">
              ไม่มีรางวัลให้เลือก
            </li>
          </ul>
        </div>
        <!-- Situations Tab -->
        <div v-if="activeTab === 'situations' && hasSituation" class="flex flex-col gap-3">
          <div class="bg-gray-600 rounded-md p-3 max-h-64 overflow-y-auto scrollbar-thin border border-gray-600">
            <h3 class="text-sm font-semibold text-gray-300 mb-2">สถานการณ์ที่พบ</h3>
            <div
              v-for="situation in situations"
              :key="situation.name"
              @click="selectSituation(situation.name)"
              class="flex flex-col gap-2 p-2 rounded-md mb-2 last:mb-0 cursor-pointer transition-all duration-200 border"
              :class="selectedSituationName === situation.name ? 'bg-gray-700 border-l-4 border-indigo-700' : 'bg-gray-700 bg-opacity-50 hover:bg-opacity-70 border-gray-600'">
              <div class="flex items-start gap-3">
                <i class="fas fa-exclamation-circle text-yellow-600 text-lg mt-1"></i>
                <div class="flex-1">
                  <p class="text-xs font-semibold text-gray-100">{{ situation.name }}</p>
                  <p class="text-xs text-gray-400">จำนวนวิญญาณ: {{ situation.minGhosts }}-{{ situation.maxGhosts }} ตัว</p>
                </div>
              </div>
              <div v-if="situation.rewards?.length" class="ml-7">
                <p class="text-xs text-gray-300 font-semibold mb-1">รางวัลที่เป็นไปได้:</p>
                <ul class="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <li
                    v-for="(reward, index) in situation.rewards"
                    :key="index"
                    class="flex items-center gap-2 p-1 bg-gray-700 rounded-md border border-gray-600">
                    <img
                      :src="reward.img || 'default-item.png'"
                      alt="Reward"
                      class="w-8 h-8 rounded-md object-cover border-2 border-gray-500"
                      @error="handleImageError"
                    />
                    <div>
                      <p class="text-xs font-semibold" :class="reward.rarityColors?.textColor || 'text-gray-100'">
                        {{ reward.name }}
                      </p>
                      <p class="text-xs text-gray-400">
                        โอกาส: {{ (reward.dropChance / 100).toFixed(2) }}%
                      </p>
                    </div>
                  </li>
                </ul>
              </div>
              <p v-else class="ml-7 text-xs text-gray-400">ไม่มีรางวัล</p>
            </div>
            <p v-if="!situations.length" class="text-center text-gray-300 text-xs py-4">
              ไม่มีสถานการณ์ให้แสดง
            </p>
          </div>
        </div>
        <!-- Merchants Tab -->
        <div v-if="activeTab === 'merchants' && hasMerchant" class="flex flex-col gap-3">
          <div class="flex border-b border-gray-600 overflow-x-auto scrollbar-thin">
            <button
              v-for="merchant in merchants"
              :key="merchant.id"
              @click="selectedMerchantId = merchant.id"
              class="relative flex items-center gap-2 px-3 py-2 text-sm font-semibold whitespace-nowrap transition duration-200 transform"
              :class="selectedMerchantId === merchant.id
                ? 'bg-gray-600 text-gray-100 border-b-2 border-yellow-500'
                : 'text-gray-300 hover:bg-gray-600 hover:-translate-y-1'"
              aria-pressed="selectedMerchantId === merchant.id">
              <img
                :src="merchant.img || 'default-merchant.png'"
                alt="Merchant"
                class="w-6 h-6 rounded-full object-cover border border-gray-500"
                @error="handleImageError"
              />
              {{ merchant.name }}
              <span v-if="selectedMerchantId === merchant.id" class="absolute bottom-0 left-0 right-0 h-0.5 bg-yellow-500"></span>
            </button>
          </div>
          <div v-if="selectedMerchant" class="bg-gray-600 rounded-md p-3 max-h-72 overflow-y-auto scrollbar-thin border border-gray-600">
            <!-- Merchant Header -->
            <div class="flex items-center gap-3 mb-3">
              <img
                :src="selectedMerchant.img || 'default-merchant.png'"
                alt="Merchant"
                class="w-12 h-12 rounded-full object-cover border-2 border-yellow-500 shadow"
                @error="handleImageError"
              />
              <div class="flex-1">
                <div class="text-sm font-bold text-gray-100 leading-tight">{{ selectedMerchant.name }}</div>
                <div class="text-xs text-gray-400 opacity-75">{{ randomPhrase }}</div>
              </div>
            </div>
            <!-- Stats Comparison -->
            <div v-if="selectedMerchant.allowRob" class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
              <div class="bg-gray-700 rounded-md p-2 border border-gray-600">
                <div class="text-center text-gray-300 mb-2 font-semibold text-xs">พลังของพ่อค้า</div>
                <div class="grid grid-cols-4 sm:grid-cols-7 gap-2 text-center">
                  <div class="flex flex-col items-center text-gray-100">
                    <i class="fas fa-heartbeat text-red-600"></i>
                    <p class="text-xs font-bold mt-1">
                      {{ Math.floor(merchantStats.maxHp * 0.1) }}-{{ merchantStats.maxHp }} / {{ merchantStats.maxHp }}
                    </p>
                  </div>
                  <div class="flex flex-col items-center text-gray-100">
                    <i class="fas fa-fist-raised text-orange-600"></i>
                    <p class="text-xs font-bold mt-1">{{ merchantStats.atk }}</p>
                  </div>
                  <div class="flex flex-col items-center text-gray-100">
                    <i class="fas fa-running text-green-600"></i>
                    <p class="text-xs font-bold mt-1">{{ merchantStats.evasion }}%</p>
                  </div>
                  <div class="flex flex-col items-center text-gray-100">
                    <i class="fas fa-crosshairs text-yellow-600"></i>
                    <p class="text-xs font-bold mt-1">{{ merchantStats.critical }}%</p>
                  </div>
                  <div class="flex flex-col items-center text-gray-100">
                    <i class="fa-solid fa-shield-virus text-purple-600"></i>
                    <p class="text-xs font-bold mt-1">{{ merchantStats.criticalReduction }}%</p>
                  </div>
                  <div class="flex flex-col items-center text-gray-100">
                    <i class="fas fa-shield-alt text-blue-600"></i>
                    <p class="text-xs font-bold mt-1">{{ merchantStats.defense }}</p>
                  </div>
                  <div class="flex flex-col items-center text-gray-100">
                    <i class="fa-solid fa-person-rays text-indigo-600"></i>
                    <p class="text-xs font-bold mt-1">{{ merchantStats.damageReduction }}%</p>
                  </div>
                </div>
              </div>
              <div class="bg-gray-700 rounded-md p-2 border border-gray-600">
                <div class="text-center text-gray-300 mb-2 font-semibold text-xs">พลังของคุณ</div>
                <div class="grid grid-cols-4 sm:grid-cols-7 gap-2 text-center">
                  <div class="flex flex-col items-center text-gray-100">
                    <i class="fas fa-heartbeat text-red-600"></i>
                    <p class="text-xs font-bold mt-1">{{ currentHealth }} / {{ calculatedStats.maxHp }}</p>
                  </div>
                  <div class="flex flex-col items-center text-gray-100">
                    <i class="fas fa-fist-raised text-orange-600"></i>
                    <p class="text-xs font-bold mt-1">{{ calculatedStats.atk }}</p>
                  </div>
                  <div class="flex flex-col items-center text-gray-100">
                    <i class="fas fa-running text-green-600"></i>
                    <p class="text-xs font-bold mt-1">{{ calculatedStats.evasion }}%</p>
                  </div>
                  <div class="flex flex-col items-center text-gray-100">
                    <i class="fas fa-crosshairs text-yellow-600"></i>
                    <p class="text-xs font-bold mt-1">{{ calculatedStats.critical }}%</p>
                  </div>
                  <div class="flex flex-col items-center text-gray-100">
                    <i class="fa-solid fa-shield-virus text-purple-600"></i>
                    <p class="text-xs font-bold mt-1">{{ calculatedStats.criticalReduction }}%</p>
                  </div>
                  <div class="flex flex-col items-center text-gray-100">
                    <i class="fas fa-shield-alt text-blue-600"></i>
                    <p class="text-xs font-bold mt-1">{{ calculatedStats.defense }}</p>
                  </div>
                  <div class="flex flex-col items-center text-gray-100">
                    <i class="fa-solid fa-person-rays text-indigo-600"></i>
                    <p class="text-xs font-bold mt-1">{{ calculatedStats.damageReduction }}%</p>
                  </div>
                </div>
              </div>
            </div>
            <!-- Rob Button -->
            <div v-if="selectedMerchant && selectedMerchant.allowRob" class="mt-3">
              <button
                @click="handleRobClick(selectedMerchantId)"
                class="w-full px-4 py-3 text-white text-sm rounded-md font-semibold transition duration-200 flex items-center justify-center gap-2 group shadow-md"
                :class="isConfirmingRob ? 'bg-red-600 hover:bg-red-700' : 'bg-indigo-600 hover:bg-indigo-700'"
                :disabled="isFighting">
                <i class="fas fa-mask text-sm"></i>
                <span class="font-bold">{{ isConfirmingRob ? 'ยืนยันปล้น' : 'ปล้น' }} {{ selectedMerchant.name }}</span>
                <span v-if="selectedMerchant.robRewards.silver" class="ml-2">{{ selectedMerchant.robRewards.silver }} <i class="fas fa-coins text-white"></i></span>
                <span v-if="selectedMerchant.robRewards.gold" class="ml-2">{{ selectedMerchant.robRewards.gold }} <i class="fas fa-g text-yellow-600"></i></span>
                <span v-if="selectedMerchant.robRewards.diamond" class="ml-2">{{ selectedMerchant.robRewards.diamond }} <i class="fas fa-gem text-blue-600"></i></span>
              </button>
            </div>
            <div class="bg-gray-700 bg-opacity-80 border border-gray-600 rounded-md px-3 py-2 my-2 shadow-sm">
              <div class="flex items-center justify-between">
                <div class="flex items-center gap-3 text-xs sm:text-sm text-gray-300">
                  <span class="inline-flex items-center gap-1"><i class="fas fa-coins text-white"></i>{{ characterData?.silver || 0 }}</span>
                  <span class="inline-flex items-center gap-1"><i class="fas fa-g text-yellow-600"></i>{{ characterData?.gold || 0 }}</span>
                  <span class="inline-flex items-center gap-1"><i class="fas fa-gem text-blue-600"></i>{{ characterData?.diamond || 0 }}</span>
                </div>
              </div>
            </div>
            <!-- Items List -->
            <transition-group name="fade-list" tag="ul" class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
              <li
                v-for="(item, index) in selectedMerchant.items"
                :key="item.name + index"
                @click="selectedItemName = item.name"
                class="group relative p-3 bg-gray-700 rounded-lg border border-gray-600 cursor-pointer transition duration-200 transform hover:-translate-y-1 hover:shadow-md hover:border-indigo-500"
                :class="selectedItemName === item.name ? 'ring-2 ring-indigo-500 bg-gray-800' : ''">
                <div class="flex items-center gap-3">
                  <img
                    :src="item.img || 'default-item.png'"
                    alt="Item"
                    class="w-12 h-12 rounded-md object-cover border-2 flex-shrink-0"
                    :class="item.rarityColors?.borderColor || 'border-gray-500'"
                    @error="handleImageError"
                  />
                  <div class="flex-1 min-w-0">
                    <div class="text-xs sm:text-sm" :class="item.rarityColors?.textColor || 'text-gray-100'">
                      {{ item.name }}
                      <i v-if="characterPrivateData?.learnedSkills?.includes(item.name)" class="fas fa-book text-green-500 ml-1 text-xs"></i>
                      <i v-if="item.transferable" class="fa-solid fa-arrow-right-arrow-left text-green-500 ml-1 text-xs"></i>
                    </div>
                    <div class="flex items-center gap-1 mt-1">
                      <span class="text-xs font-bold text-gray-100">{{ item.price }}</span>
                      <i v-if="item.currency === 'silver'" class="fas fa-coins text-xs text-gray-100"></i>
                      <i v-else-if="item.currency === 'gold'" class="fas fa-g text-xs text-yellow-600"></i>
                      <i v-else-if="item.currency === 'diamond'" class="fas fa-gem text-xs text-blue-600"></i>
                    </div>
                  </div>
                </div>
                <button
                  class="mt-3 w-full flex items-center justify-center gap-2 text-xs text-white rounded-md py-1.5 transition-opacity duration-200 transform translate-y-1"
                  :class="isConfirmingBuy && selectedItemToBuy === item.name ? 'bg-red-600 hover:bg-red-700' : 'bg-indigo-600 hover:bg-indigo-700'"
                  :disabled="isFighting"
                  @click.stop="handleBuyClick(selectedMerchantId, item.name)"
                  title="ซื้อทันที">
                  <i class="fas fa-cart-plus text-xs"></i>
                  {{ isConfirmingBuy && selectedItemToBuy === item.name ? 'ยืนยันซื้อ' : 'ซื้อทันที' }}
                </button>
              </li>
            </transition-group>
          </div>
          <p v-if="!merchants.length" class="text-center text-gray-300 text-xs py-4">
            ไม่มีพ่อค้าให้แสดง
          </p>
        </div>
        <!-- Boss Ghosts Tab -->
        <div v-if="activeTab === 'bossGhosts' && hasBossGhost" class="flex flex-col gap-3">
          <div class="bg-gray-600 rounded-md p-3 max-h-64 overflow-y-auto scrollbar-thin border border-gray-600">
            <h3 class="text-sm font-semibold text-gray-300 mb-2">บอสวิญญาณที่พบ</h3>
            <div
              v-for="boss in bossGhosts"
              :key="boss.id"
              @click="selectBossGhost(boss.id)"
              class="flex flex-col gap-2 p-2 rounded-md mb-2 last:mb-0 cursor-pointer transition-all duration-200 border"
              :class="selectedBossGhostId === boss.id ? 'bg-gray-700 border-l-4 border-red-700' : 'bg-gray-700 bg-opacity-50 hover:bg-opacity-70 border-gray-600'">
              <div class="flex items-center gap-3">
                <div class="relative">
                  <img
                    :src="boss.imageUrl || 'default-ghost.png'"
                    alt="Boss Ghost"
                    class="w-10 h-10 rounded-md object-cover border-2 border-red-600"
                    @error="handleImageError"
                  />
                </div>
                <div class="flex-1">
                  <p class="text-xs font-semibold text-red-300">{{ boss.name }}</p>
                  <p class="text-xs text-gray-400">เลเวล: {{ boss.level || 'N/A' }}</p>
                  <p class="text-xxs text-yellow-400 mt-1 italic">💡 กดปุ่มด้านล่างเพื่อเริ่มสู้ทันที! (ไม่ต้องสำรวจต่อ)</p>
                </div>
              </div>
              <button
                class="w-full px-4 py-2 text-white text-sm rounded-md font-semibold transition-all duration-200 flex items-center justify-center gap-2 group shadow-md border-2 border-red-600"
                :class="confirmingBossId === boss.id ? 'bg-red-700 hover:bg-red-800 animate-pulse' : 'bg-red-600 hover:bg-red-700'"
                :disabled="isFighting"
                @click.stop="handleFightBossClick(boss.id)">
                <i class="fas fa-skull-crossbones text-xs group-hover:animate-pulse"></i>
                {{ confirmingBossId === boss.id ? 'ยืนยันสู้กับบอส' : 'สู้กับบอสเดี๋ยวนี้!' }}
              </button>
              <div v-if="boss.stats" class="ml-7 bg-gray-800 bg-opacity-30 p-2 rounded-md">
                <p class="text-xs text-gray-300 font-semibold mb-1">พลังของบอส:</p>
                <div class="grid grid-cols-4 sm:grid-cols-7 gap-2 text-center">
                  <div class="flex flex-col items-center text-gray-100">
                    <i class="fas fa-heartbeat text-red-600"></i>
                    <p class="text-xs font-bold mt-1">{{ bossStats(boss).maxHp }}</p>
                  </div>
                  <div class="flex flex-col items-center text-gray-100">
                    <i class="fas fa-fist-raised text-orange-600"></i>
                    <p class="text-xs font-bold mt-1">{{ bossStats(boss).atk }}</p>
                  </div>
                  <div class="flex flex-col items-center text-gray-100">
                    <i class="fas fa-running text-green-600"></i>
                    <p class="text-xs font-bold mt-1">{{ bossStats(boss).evasion }}%</p>
                  </div>
                  <div class="flex flex-col items-center text-gray-100">
                    <i class="fas fa-crosshairs text-yellow-600"></i>
                    <p class="text-xs font-bold mt-1">{{ bossStats(boss).critical }}%</p>
                  </div>
                  <div class="flex flex-col items-center text-gray-100">
                    <i class="fa-solid fa-shield-virus text-purple-600"></i>
                    <p class="text-xs font-bold mt-1">{{ bossStats(boss).criticalReduction }}%</p>
                  </div>
                  <div class="flex flex-col items-center text-gray-100">
                    <i class="fas fa-shield-alt text-blue-600"></i>
                    <p class="text-xs font-bold mt-1">{{ bossStats(boss).defense }}</p>
                  </div>
                  <div class="flex flex-col items-center text-gray-100">
                    <i class="fa-solid fa-person-rays text-indigo-600"></i>
                    <p class="text-xs font-bold mt-1">{{ bossStats(boss).damageReduction }}%</p>
                  </div>
                </div>
              </div>
              <div v-if="boss.drops?.length" class="ml-7">
                <p class="text-xs text-gray-300 font-semibold mb-1">รางวัลที่เป็นไปได้:</p>
                <ul class="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <li
                    v-for="(drop, index) in boss.drops"
                    :key="index"
                    class="flex items-center gap-2 p-1 bg-gray-700 rounded-md border border-gray-600">
                    <img
                      :src="drop.img || 'default-item.png'"
                      alt="Drop"
                      class="w-8 h-8 rounded-md object-cover border-2 border-gray-500"
                      @error="handleImageError"
                    />
                    <div>
                      <p class="text-xs font-semibold" :class="drop.rarityColors?.textColor || 'text-gray-100'">
                        {{ drop.name }}
                      </p>
                      <p class="text-xs text-gray-400">
                        โอกาส: {{ (drop.dropChance / 100).toFixed(2) }}%
                      </p>
                    </div>
                  </li>
                </ul>
              </div>
              <p v-else class="ml-7 text-xs text-gray-400">ไม่มีรางวัล</p>
            </div>
            <p v-if="!bossGhosts.length" class="text-center text-gray-300 text-xs py-4">
              ไม่มีบอสวิญญาณให้แสดง
            </p>
          </div>
        </div>
      </div>
      <!-- Player Section (Bars only when no opponent) -->
      <div v-if="!opponentData && !isLoading" class="flex flex-col items-center gap-3 bg-gray-900 border border-gray-700 rounded-md p-4">
        <div class="w-full flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <div class="relative flex-1">
            <div class="bg-gray-500 h-5 rounded overflow-hidden">
              <div class="bg-red-600 h-5 rounded transition-all duration-200"
                   :style="{ width: `${(currentHealth / calculatedStats.maxHp * 100) || 0}%` }"></div>
              <p class="absolute inset-0 flex items-center justify-center text-xs text-gray-100">
                HP: {{ currentHealth }} / {{ calculatedStats.maxHp }}
              </p>
            </div>
          </div>
          <div class="relative flex-1">
            <div class="bg-gray-500 h-5 rounded overflow-hidden">
              <div class="bg-blue-600 h-5 rounded transition-all duration-200"
                   :style="{ width: `${(energyLevel / maxEnergy * 100) || 0}%` }"></div>
              <p class="absolute inset-0 flex items-center justify-center text-xs text-gray-100">
                พลังงาน: {{ energyLevel || 0 }} / {{ maxEnergy }}
              </p>
            </div>
          </div>
        </div>
      </div>
      <!-- Action Buttons -->
      <div class="flex flex-col gap-2">
        <button
          v-if="opponentData"
          @click="attackOpponentHandler()"
          class="w-full px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm rounded-md font-semibold transition-all duration-200 flex items-center justify-center gap-2 group"
          :disabled="isFighting || (opponentType === 'ghost' && energyLevel <= 0) || isAutoAttacking">
          <div v-if="isFighting && !isAutoAttacking" class="animate-spin-pulse rounded-full h-4 w-4 border-t-2 border-white"></div>
          <i v-else class="fas fa-skull-crossbones text-xs group-hover:animate-pulse"></i>
          โจมตี
        </button>
        <div v-if="opponentData" class="flex gap-2">
          <button
            @click="toggleAutoAttack(250)"
            class="w-full px-4 py-2 text-white text-sm rounded-md font-semibold transition-all duration-200 flex items-center justify-center gap-2 group"
            :class="isAutoAttacking && autoAttackIntervalMs === 250 ? 'bg-red-600 hover:bg-red-700' : 'bg-indigo-600 hover:bg-indigo-700'"
            :disabled="!isAutoAttacking && (isFighting || (opponentType === 'ghost' && energyLevel <= 0) || !opponentData)">
            <div v-if="isFighting && isAutoAttacking && autoAttackIntervalMs === 250" class="animate-spin-pulse rounded-full h-4 w-4 border-t-2 border-white"></div>
            <i v-else class="fas fa-robot text-xs group-hover:animate-pulse"></i>
            {{ isAutoAttacking && autoAttackIntervalMs === 250 ? '(0.25s)' : '(0.25s)' }}
          </button>
          <button
            @click="toggleAutoAttack(500)"
            class="w-full px-4 py-2 text-white text-sm rounded-md font-semibold transition-all duration-200 flex items-center justify-center gap-2 group"
            :class="isAutoAttacking && autoAttackIntervalMs === 500 ? 'bg-red-600 hover:bg-red-700' : 'bg-indigo-600 hover:bg-indigo-700'"
            :disabled="!isAutoAttacking && (isFighting || (opponentType === 'ghost' && energyLevel <= 0) || !opponentData)">
            <div v-if="isFighting && isAutoAttacking && autoAttackIntervalMs === 500" class="animate-spin-pulse rounded-full h-4 w-4 border-t-2 border-white"></div>
            <i v-else class="fas fa-robot text-xs group-hover:animate-pulse"></i>
            {{ isAutoAttacking && autoAttackIntervalMs === 500 ? '(0.5s)' : '(0.5s)' }}
          </button>
          <button
            @click="toggleAutoAttack(1000)"
            class="w-full px-4 py-2 text-white text-sm rounded-md font-semibold transition-all duration-200 flex items-center justify-center gap-2 group"
            :class="isAutoAttacking && autoAttackIntervalMs === 1000 ? 'bg-red-600 hover:bg-red-700' : 'bg-indigo-600 hover:bg-indigo-700'"
            :disabled="!isAutoAttacking && (isFighting || (opponentType === 'ghost' && energyLevel <= 0) || !opponentData)">
            <div v-if="isFighting && isAutoAttacking && autoAttackIntervalMs === 1000" class="animate-spin-pulse rounded-full h-4 w-4 border-t-2 border-white"></div>
            <i v-else class="fas fa-robot text-xs group-hover:animate-pulse"></i>
            {{ isAutoAttacking && autoAttackIntervalMs === 1000 ? '(1s)' : '(1s)' }}
          </button>
        </div>
        <button
          v-if="opponentData && !showFleeOptions"
          @click="showFleeOptions = true"
          class="w-full px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm rounded-md font-semibold transition-all duration-200 flex items-center justify-center gap-2 group"
          :disabled="isFighting || isAutoAttacking">
          <div v-if="isFighting" class="animate-spin-pulse rounded-full h-4 w-4 border-t-2 border-white"></div>
          <i v-else class="fas fa-running text-xs group-hover:animate-bounce"></i>
          หนี
        </button>
        <button
          v-if="opponentData && showFleeOptions"
          @click="fleeOpponentHandler()"
          class="w-full px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm rounded-md font-semibold transition-all duration-200 flex items-center justify-center gap-2 group"
          :disabled="isFighting || isAutoAttacking">
          <div v-if="isFighting && !isAutoAttacking" class="animate-spin-pulse rounded-full h-4 w-4 border-t-2 border-white"></div>
          <i v-else class="fas fa-running text-xs group-hover:animate-bounce"></i>
          หนี (โอกาสสำเร็จ {{ fleeChance }}%)
        </button>
        <button
          v-if="opponentData && showFleeOptions && eventData.escapeAttempts > 0 && opponentType !== 'merchant'"
          @click="fleeBySilver"
          class="w-full px-4 py-2 bg-yellow-600 hover:bg-yellow-700 text-white text-sm rounded-md font-semibold transition-all duration-200 flex items-center justify-center gap-2 group"
          :disabled="isFighting || isAutoAttacking">
          <div v-if="isFighting && !isAutoAttacking" class="animate-spin-pulse rounded-full h-4 w-4 border-t-2 border-white"></div>
          <i v-else class="fas fa-running text-xs group-hover:animate-bounce"></i>
          หนี {{ eventData.escapeCoins }} เหรียญ (โอกาสสำเร็จ 60%)
        </button>
        <button
          v-if="!opponentData && hasReward && selectedRewardIds.length > 0 && activeTab === 'rewards'"
          @click="addToBag"
          class="w-full px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm rounded-md font-semibold transition-all duration-200 flex items-center justify-center gap-2 group"
          :disabled="isFighting || isAddingToBag || selectedWeight > capacity">
          <div v-if="isFighting || isAddingToBag" class="animate-spin-pulse rounded-full h-4 w-4 border-t-2 border-white"></div>
          <i v-else class="fas fa-shopping-bag text-xs group-hover:animate-pulse"></i>
          นำใส่กระเป๋า ({{ selectedRewardIds.length }} รายการ)
        </button>
        <button
          v-if="!opponentData && hasSituation && activeTab === 'situations' && selectedSituationName"
          @click="continueExploringSituation"
          class="w-full px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm rounded-md font-semibold transition-all duration-200 flex items-center justify-center gap-2 group"
          :disabled="isFighting || isActionsDisabled">
          <div v-if="isFighting" class="animate-spin-pulse rounded-full h-4 w-4 border-t-2 border-white"></div>
          <i v-else class="fas fa-map text-xs group-hover:animate-pulse"></i>
          สำรวจสถานการณ์ {{ selectedSituationName }}
        </button>
        <button
          v-if="!opponentData"
          @click="handleContinueExploringClick"
          class="w-full px-4 py-2 text-white text-sm rounded-md font-semibold transition-all duration-200 flex items-center justify-center gap-2 group"
          :class="isConfirmingExplore ? 'bg-red-600 hover:bg-red-700' : 'bg-yellow-600 hover:bg-yellow-700'"
          :disabled="isFighting || isActionsDisabled">
          <div v-if="isFighting" class="animate-spin-pulse rounded-full h-4 w-4 border-t-2 border-white"></div>
          <i v-else class="fas fa-map text-xs group-hover:animate-pulse"></i>
          {{ isConfirmingExplore ? 'ยืนยันสำรวจต่อ' : 'สำรวจต่อ' }}
        </button>
        <button
          v-if="!opponentData"
          @click="handleGoHomeClick"
          class="w-full px-4 py-2 text-white text-sm rounded-md font-semibold transition-all duration-200 flex items-center justify-center gap-2 group"
          :class="isConfirmingGoHome ? 'bg-red-600 hover:bg-red-700' : 'bg-blue-600 hover:bg-blue-700'"
          :disabled="isFighting || isActionsDisabled || !canGoHome">
          <div v-if="isFighting" class="animate-spin-pulse rounded-full h-4 w-4 border-t-2 border-white"></div>
          <i v-else class="fas fa-home text-xs group-hover:animate-bounce"></i>
          {{ isConfirmingGoHome ? 'ยืนยันกลับบ้าน' : 'กลับบ้าน' }}
        </button>
      </div>
      <!-- Fight Result -->
      <p v-if="fightResult" class="text-xs text-gray-300 text-center">{{ fightResult }}</p>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, computed, onUnmounted, watch } from 'vue';
import { projectFunctions, projectFirestore } from '@/firebase/config';
import getUser from '@/composables/getUser';
import { usePlayerStore } from '@/composables/usePlayerStore';
import { useInventoryStore } from '@/composables/useInventoryStore';
import gameItemsData from '@/data/gameItems.json';

export default {
  props: {
    eventId: {
      type: String,
      required: true,
    },
  },
  emits: ['fightCompleted', 'close'],
  setup(props, { emit }) {
    const { user } = getUser();
    const ghostActions = projectFunctions.httpsCallable('gameActions');
    const exploreSupernatural = projectFunctions.httpsCallable('gameActions');
    const gameActions = projectFunctions.httpsCallable('gameActions');
    const inventoryStore = useInventoryStore();
    const playerStore = usePlayerStore();

    // ===== Reactive States =====
    
    const isLoading = ref(true);
    const isLoadingRewards = ref(false);
    const ghostData = ref(null);
    const merchantData = ref(null);
    const characterData = computed(() => playerStore.characterData);
    const characterPrivateData = computed(() => playerStore.characterPrivateData);
    const currentOpponentHp = ref(0);
    const fightResult = ref('');
    const isFighting = ref(false);
    const hasReward = ref(false);
    const rewardItemIds = ref([]);
    const rewardDataMap = ref({});
    const hasSituation = ref(false);
    const hasMerchant = ref(false);
    const hasBossGhost = ref(false);
    const situations = ref([]);
    const selectedSituationName = ref(null);
    const ghostKilled = ref(0);
    const rewards = ref([]);
    const selectedRewardIds = ref([]);
    const merchants = ref([]);
    const selectedMerchantId = ref(null);
    const selectedItemName = ref(null);
    const expiresAt = ref(null);
    const timeLeft = ref(null);
    const energyLevel = computed(() => playerStore.characterPrivateData?.energyLevel || 0);
    const maxEnergy = ref(4800);
    const currentWeight = ref(0);
    const maxLoad = ref(0);
    const isAutoAttacking = ref(false);
    const autoAttackIntervalMs = ref(null);
    const eventData = ref({ escapeCoins: 0, escapeAttempts: 0, diamondReward: 0 });
    const showFleeOptions = ref(false);
    const isConfirmingGoHome = ref(false);
    const isConfirmingRob = ref(false);
    const isConfirmingExplore = ref(false);
    const activeTab = ref('rewards');
    const mapName = ref('');
    const currentMerchantId = ref(null);
    const canGoHome = ref(true);
    const bossGhosts = ref([]);
    const selectedBossGhostId = ref(null);
    const confirmingBossId = ref(null);
    const isClosed = ref(false);
    const isConfirmingBuy = ref(false);
    const selectedItemToBuy = ref(null);
    const isAddingToBag = ref(false);
    const isActionsDisabled = ref(false);

    watch(() => inventoryStore.bagData, (bagData) => {
      if (bagData) {
        currentWeight.value = bagData.currentWeight || 0;
        maxLoad.value = bagData.maxLoad || 100;
      }
    }, { immediate: true });

    // ✅ Boss status bar — real-time
    const mapBossStatuses = ref({}); // { [bossName]: { isAlive, progress, imageUrl } }
    const bossImageCache = {};
    let bossStatusUnsubscribes = [];
    let bossCountdownInterval = null;

    // ===== Timers & Unsubscribes =====
    let timer = null;
    let confirmTimeout = null;
    let confirmRobTimeout = null;
    let confirmExploreTimeout = null;
    let confirmBossTimeout = null;
    let confirmBuyTimeout = null;
    let goHomeUnlockTimeout = null;
    let unsubscribeEvent = null;
    let unsubscribeGhost = null;
    let unsubscribeMerchant = null;
    let unsubscribeMerchants = ref([]);
    let unsubscribeBossGhosts = ref([]);

    let autoAttackInterval = null;
    let noGhostSince = null;
    const NO_GHOST_GRACE_MS = 30000;
    const resetEmbeddedEventSubscriptions = () => {
      if (unsubscribeEvent) {
        unsubscribeEvent();
        unsubscribeEvent = null;
      }
      if (unsubscribeGhost) {
        unsubscribeGhost();
        unsubscribeGhost = null;
      }
      if (unsubscribeMerchant) {
        unsubscribeMerchant();
        unsubscribeMerchant = null;
      }
      unsubscribeMerchants.value.forEach((unsub) => unsub());
      unsubscribeBossGhosts.value.forEach((unsub) => unsub());
      unsubscribeMerchants.value = [];
      unsubscribeBossGhosts.value = [];
    };
    const resetEmbeddedEventState = () => {
      if (goHomeUnlockTimeout) {
        clearTimeout(goHomeUnlockTimeout);
        goHomeUnlockTimeout = null;
      }
      eventData.value = { escapeCoins: 0, escapeAttempts: 0, diamondReward: 0 };
      ghostData.value = null;
      merchantData.value = null;
      currentOpponentHp.value = 0;
      rewards.value = [];
      merchants.value = [];
      bossGhosts.value = [];
      situations.value = [];
      rewardItemIds.value = [];
      rewardDataMap.value = {};
      selectedRewardIds.value = [];
      selectedSituationName.value = null;
      selectedMerchantId.value = null;
      selectedItemName.value = null;
      selectedBossGhostId.value = null;
      currentMerchantId.value = null;
      hasReward.value = false;
      hasSituation.value = false;
      hasMerchant.value = false;
      hasBossGhost.value = false;
      canGoHome.value = true;
      showFleeOptions.value = false;
      mapName.value = '';
    };
    const syncGoHomeAvailability = () => {
  canGoHome.value = !goHomeUnlockTimeout;
};
    const lockGoHomeTemporarily = (duration = 2000) => {
      canGoHome.value = false;
      if (goHomeUnlockTimeout) {
        clearTimeout(goHomeUnlockTimeout);
      }
      goHomeUnlockTimeout = setTimeout(() => {
        goHomeUnlockTimeout = null;
        syncGoHomeAvailability();
      }, duration);
    };

    // ===== Boss Status Bar Helpers =====
    const unsubscribeMapBossStatuses = () => {
      bossStatusUnsubscribes.forEach(fn => fn());
      bossStatusUnsubscribes = [];
      if (bossCountdownInterval) {
        clearInterval(bossCountdownInterval);
        bossCountdownInterval = null;
      }
    };

    // Subscribe บอสทุกตัวของแผนที่ปัจจุบัน — เรียกเมื่อรู้ mapName แล้ว
    const subscribeMapBossStatuses = async (currentMapName, bossGhostNames) => {
      unsubscribeMapBossStatuses();
      if (!currentMapName || !bossGhostNames?.length) return;

      // Fetch imageUrl จาก gameGhosts (cache)
      bossGhostNames.forEach(bossName => {
        if (!bossImageCache[bossName]) {
          projectFirestore.collection('gameGhosts').doc(bossName).get()
            .then(doc => {
              bossImageCache[bossName] = doc.exists ? (doc.data().imageUrl || null) : null;
              if (mapBossStatuses.value[bossName]) {
                mapBossStatuses.value = {
                  ...mapBossStatuses.value,
                  [bossName]: { ...mapBossStatuses.value[bossName], imageUrl: bossImageCache[bossName] }
                };
              }
            })
            .catch(() => { bossImageCache[bossName] = null; });
        }
      });

      bossGhostNames.forEach(bossName => {
        const docId = `${currentMapName}_${bossName}`;
        const unsub = projectFirestore
          .collection('bossGhostStatus')
          .doc(docId)
          .onSnapshot(doc => {
            const data        = doc.exists ? doc.data() : {};
            const isAlive     = data.isAlive ?? true;
            const nextRespawn = data.nextRespawn ? data.nextRespawn.toMillis() : null;
            const updatedAt   = data.updatedAt   ? data.updatedAt.toMillis()   : null;
            const respawnDuration = (nextRespawn && updatedAt && nextRespawn > updatedAt)
              ? nextRespawn - updatedAt
              : 10800000;
            const diff     = nextRespawn ? Math.max(0, nextRespawn - Date.now()) : 0;
            const progress = isAlive ? 1 : (respawnDuration > 0 ? Math.max(0, Math.min(1, 1 - (diff / respawnDuration))) : 0);
            mapBossStatuses.value = {
              ...mapBossStatuses.value,
              [bossName]: {
                isAlive,
                nextRespawn,
                respawnDuration,
                progress,
                imageUrl: bossImageCache[bossName] ?? null,
              }
            };
          });
        bossStatusUnsubscribes.push(unsub);
      });

      // Tick progress ทุก 1 วินาที
      bossCountdownInterval = setInterval(() => {
        const updated = { ...mapBossStatuses.value };
        let changed = false;
        Object.keys(updated).forEach(bossName => {
          const s = updated[bossName];
          if (!s.isAlive && s.nextRespawn) {
            const diff       = Math.max(0, s.nextRespawn - Date.now());
            const newAlive   = diff <= 0;
            const newProgress = newAlive ? 1 : (s.respawnDuration > 0 ? Math.max(0, Math.min(1, 1 - (diff / s.respawnDuration))) : 0);
            if (newProgress !== s.progress || newAlive !== s.isAlive) {
              updated[bossName] = { ...s, isAlive: newAlive, progress: newProgress };
              changed = true;
            }
          }
        });
        if (changed) mapBossStatuses.value = updated;
      }, 1000);
    };

    // เรียงบอสที่เกิดแล้วขึ้นก่อน ตายลงหลัง
    const sortedMapBossStatuses = computed(() => {
      const entries = Object.entries(mapBossStatuses.value);
      entries.sort(([, a], [, b]) => {
        if (a.isAlive && !b.isAlive) return -1;
        if (!a.isAlive && b.isAlive) return 1;
        return 0;
      });
      return Object.fromEntries(entries);
    });
    const opponentType = computed(() => {
      if (ghostData.value) return 'ghost';
      if (merchantData.value) return 'merchant';
      return null;
    });
    const opponentData = computed(() => ghostData.value || merchantData.value);
    const opponentStats = computed(() => {
      if (!opponentData.value) {
        return { maxHp: 100, atk: 0, critical: 0, evasion: 0, criticalReduction: 0, defense: 0, damageReduction: 0, flee: 0 };
      }
      const stats = opponentData.value.stats || {};
      const luck = stats.luck || 0;
      const defense = stats.defense || 0;
      const defenseBonus = Math.floor(defense / 20);
      return {
        maxHp: (stats.stamina || 0) * 20 + (stats.maxHp || 0),
        atk: (stats.str || 0) * 2 + (stats.atk || 0),
        critical: Math.floor(((stats.int || 0) / 2) + (stats.critical || 0)),
        evasion: Math.floor(((stats.agi || 0) / 2) + (stats.evasion || 0)),
        criticalReduction: Math.floor(luck) + defenseBonus,
        defense: defense,
        damageReduction: defenseBonus,
        flee: Math.floor(((stats.agi || 0) / 2 + (stats.int || 0) / 2) / 2),
      };
    });
    const selectedMerchant = computed(() =>
      merchants.value.find((m) => m.id === selectedMerchantId.value) || null
    );
    const calculatedStats = computed(() => {
      if (!characterPrivateData.value) {
        return { maxHp: 100, atk: 0, critical: 0, evasion: 0, criticalReduction: 0, defense: 0, damageReduction: 0, flee: 0 };
      }
      const stats = characterPrivateData.value;
      const str = (stats.itemStats?.str||0)+(stats.bonusStats?.str||0)+(stats.stats?.str||0)+(stats.traitStats?.str||0)+(stats.skillStats?.str||0)+(stats.titleStats?.str||0)+(stats.tattooStats?.str||0)+(stats.ghostStats?.str||0);
      const stamina = (stats.itemStats?.stamina||0)+(stats.bonusStats?.stamina||0)+(stats.stats?.stamina||0)+(stats.traitStats?.stamina||0)+(stats.skillStats?.stamina||0)+(stats.titleStats?.stamina||0)+(stats.tattooStats?.stamina||0)+(stats.ghostStats?.stamina||0);
      const int_ = (stats.itemStats?.int||0)+(stats.bonusStats?.int||0)+(stats.stats?.int||0)+(stats.traitStats?.int||0)+(stats.skillStats?.int||0)+(stats.titleStats?.int||0)+(stats.tattooStats?.int||0)+(stats.ghostStats?.int||0);
      const agi = (stats.itemStats?.agi||0)+(stats.bonusStats?.agi||0)+(stats.stats?.agi||0)+(stats.traitStats?.agi||0)+(stats.skillStats?.agi||0)+(stats.titleStats?.agi||0)+(stats.tattooStats?.agi||0)+(stats.ghostStats?.agi||0);
      const luck = (stats.itemStats?.luck||0)+(stats.bonusStats?.luck||0)+(stats.stats?.luck||0)+(stats.traitStats?.luck||0)+(stats.skillStats?.luck||0)+(stats.titleStats?.luck||0)+(stats.tattooStats?.luck||0)+(stats.ghostStats?.luck||0);
      const defense = (stats.itemStats?.defense||0)+(stats.bonusStats?.defense||0)+(stats.stats?.defense||0)+(stats.traitStats?.defense||0)+(stats.skillStats?.defense||0)+(stats.titleStats?.defense||0)+(stats.tattooStats?.defense||0)+(stats.ghostStats?.defense||0);
      const flatMaxHp = (stats.itemStats?.maxHp||0)+(stats.bonusStats?.maxHp||0)+(stats.stats?.maxHp||0)+(stats.traitStats?.maxHp||0)+(stats.skillStats?.maxHp||0)+(stats.titleStats?.maxHp||0)+(stats.tattooStats?.maxHp||0)+(stats.ghostStats?.maxHp||0);
      const flatAtk = (stats.itemStats?.atk||0)+(stats.bonusStats?.atk||0)+(stats.stats?.atk||0)+(stats.traitStats?.atk||0)+(stats.skillStats?.atk||0)+(stats.titleStats?.atk||0)+(stats.tattooStats?.atk||0)+(stats.ghostStats?.atk||0);
      const flatCritical = (stats.itemStats?.critical||0)+(stats.bonusStats?.critical||0)+(stats.stats?.critical||0)+(stats.traitStats?.critical||0)+(stats.skillStats?.critical||0)+(stats.titleStats?.critical||0)+(stats.tattooStats?.critical||0)+(stats.ghostStats?.critical||0);
      const flatEvasion = (stats.itemStats?.evasion||0)+(stats.bonusStats?.evasion||0)+(stats.stats?.evasion||0)+(stats.traitStats?.evasion||0)+(stats.skillStats?.evasion||0)+(stats.titleStats?.evasion||0)+(stats.tattooStats?.evasion||0)+(stats.ghostStats?.evasion||0);
      const defenseBonus = Math.floor(defense / 20);
      return {
        maxHp: stamina*20+flatMaxHp,
        atk: str*2+flatAtk,
        critical: Math.floor((int_/2)+flatCritical),
        evasion: Math.floor((agi/2)+flatEvasion),
        criticalReduction: Math.floor(luck)+defenseBonus,
        defense: defense,
        damageReduction: defenseBonus,
        flee: Math.floor(((agi/2)+(int_/2))/2),
      };
    });
    const currentHealth = computed(() => {
      const damage = characterPrivateData.value?.damageTaken || 0;
      return Math.max(0, calculatedStats.value.maxHp - damage);
    });
    const fleeChance = computed(() => {
      if (!opponentData.value || !characterPrivateData.value) return 10;
      const playerFlee = calculatedStats.value.flee;
      const opponentFlee = opponentStats.value.flee || 0;
      const fleeBase = Math.max(0, playerFlee - opponentFlee);
      return Math.min(100, 10 + Math.floor(fleeBase / 2));
    });
    const selectedWeight = computed(() =>
      rewards.value.filter(r => selectedRewardIds.value.includes(r.id)).reduce((s, r) => s + (r.weight || 0), 0)
    );
    const capacity = computed(() => Math.max(0, (maxLoad.value || 0) - (currentWeight.value || 0)));
    const rarityColors = computed(() => (rarity) => {
      const map = {
        1: { bgColor:'bg-gray-500', textColor:'text-gray-500', borderColor:'border-gray-600' },
        2: { bgColor:'bg-stone-500', textColor:'text-stone-500', borderColor:'border-stone-600' },
        3: { bgColor:'bg-green-500', textColor:'text-green-500', borderColor:'border-green-600' },
        4: { bgColor:'bg-blue-500', textColor:'text-blue-500', borderColor:'border-blue-600' },
        5: { bgColor:'bg-red-500', textColor:'text-red-500', borderColor:'border-red-600' },
        6: { bgColor:'bg-purple-500', textColor:'text-purple-500', borderColor:'border-purple-600' },
        7: { bgColor:'bg-orange-500', textColor:'text-orange-500', borderColor:'border-orange-600' },
        8: { bgColor:'bg-pink-500', textColor:'text-pink-500', borderColor:'border-pink-600' },
        9: { bgColor:'bg-yellow-500', textColor:'text-yellow-500', borderColor:'border-yellow-600' },
      };
      return map[rarity] || { bgColor:'bg-amber-500', textColor:'text-amber-500', borderColor:'border-amber-600' };
    });
    const randomPhrase = computed(() => {
      if (selectedMerchant.value?.phrases?.length > 0) {
        const phrases = selectedMerchant.value.phrases;
        return phrases[Math.floor(Math.random() * phrases.length)];
      }
      return 'เลือกไอเท็มด้านล่าง หรือกดซื้อทันที';
    });
    const isDanger = computed(() => (timeLeft.value ?? 0) <= 300000);
    const merchantStats = computed(() => {
      if (!selectedMerchant.value) return { maxHp:100, atk:0, critical:0, evasion:0, criticalReduction:0, defense:0, damageReduction:0 };
      const stats = selectedMerchant.value.stats || {};
      const luck = stats.luck || 0;
      const defense = stats.defense || 0;
      const defenseBonus = Math.floor(defense / 20);
      return {
        maxHp: (stats.stamina||0)*20+(stats.maxHp||0),
        atk: (stats.str||0)*2+(stats.atk||0),
        critical: Math.floor(((stats.int||0)/2)+(stats.critical||0)),
        evasion: Math.floor(((stats.agi||0)/2)+(stats.evasion||0)),
        criticalReduction: Math.floor(luck)+defenseBonus,
        defense: defense,
        damageReduction: defenseBonus,
      };
    });
    const bossStats = computed(() => (boss) => {
      const stats = boss.stats || {};
      const luck = stats.luck || 0;
      const defense = stats.defense || 0;
      const defenseBonus = Math.floor(defense / 20);
      return {
        maxHp: (stats.stamina||0)*20+(stats.maxHp||0),
        atk: (stats.str||0)*2+(stats.atk||0),
        critical: Math.floor(((stats.int||0)/2)+(stats.critical||0)),
        evasion: Math.floor(((stats.agi||0)/2)+(stats.evasion||0)),
        criticalReduction: Math.floor(luck)+defenseBonus,
        defense: defense,
        damageReduction: defenseBonus,
      };
    });

    // ===== Helpers =====
    const formatTime = (ms) => {
      if (ms <= 0) return 'หมดเวลา!';
      const seconds = Math.floor((ms / 1000) % 60);
      const minutes = Math.floor((ms / (1000 * 60)) % 60);
      return `${minutes}:${seconds < 10 ? '0' + seconds : seconds}`;
    };
    const handleImageError = (event) => {
      event.target.src = 'https://via.placeholder.com/40?text=Image+Not+Found';
    };
    const itemPreviewCache = new Map();
    const pickActiveId = (currentId, items) => {
      if (items.some((item) => item.id === currentId)) {
        return currentId;
      }
      return items[0]?.id || null;
    };
    const getEntityMaxHp = (entity) => {
      if (!entity) return 0;
      const stats = entity.stats || {};
      return (stats.stamina || 0) * 20 + (stats.maxHp || 0);
    };
    const toEmbeddedEntity = (id, data) => {
      if (!id || !data) return null;
      return { id, ...data };
    };
    const getItemTemplateByName = (itemName) => {
      if (!itemName) return null;
      return gameItemsData[itemName] || null;
    };
    const getGameItemPreview = async (itemName) => {
      if (!itemName) {
        return {
          name: '',
          img: 'default-item.png',
          rarity: 1,
          rarityColors: rarityColors.value(1),
        };
      }
      if (itemPreviewCache.has(itemName)) {
        return itemPreviewCache.get(itemName);
      }

      const itemTemplate = getItemTemplateByName(itemName);
      const preview = {
        name: itemTemplate?.name || itemName,
        img: itemTemplate?.img || itemTemplate?.imageUrl || 'default-item.png',
        rarity: itemTemplate?.rarity || 1,
        rarityColors: itemTemplate?.rarityColors || rarityColors.value(itemTemplate?.rarity || 1),
      };

      itemPreviewCache.set(itemName, preview);
      return preview;
    };
    const buildRewardEntry = (rewardId, rewardData = {}) => {
      const itemTemplate = getItemTemplateByName(rewardData.itemName || rewardData.name) || {};
      const rarity = rewardData.rarity || itemTemplate.rarity || 1;
      return {
        id: rewardData.localId || rewardId,
        ...rewardData,
        name: rewardData.name || rewardData.itemName || rewardId,
        img: rewardData.img || rewardData.imageUrl || itemTemplate.img || itemTemplate.imageUrl || 'default-item.png',
        rarity,
        rarityColors: rewardData.rarityColors || itemTemplate.rarityColors || rarityColors.value(rarity),
        weight: Number(rewardData.weight || itemTemplate.weight || 0),
        transfer: !!(rewardData.transfer ?? rewardData.transferable),
      };
    };
    const toMerchantPreview = (merchantId, merchantData = {}) => ({
      id: merchantId,
      ...merchantData,
      items: (merchantData.items || []).map((item) => {
        const itemTemplate = getItemTemplateByName(item.name || item.itemName) || {};
        const rarity = item.rarity || itemTemplate.rarity || 1;
        return {
          ...item,
          img: item.img || item.imageUrl || itemTemplate.img || itemTemplate.imageUrl || 'default-item.png',
          rarity,
          rarityColors: item.rarityColors || itemTemplate.rarityColors || rarityColors.value(rarity),
        };
      }),
      robRewards: merchantData.robRewards || { diamond: 0, gold: 0, silver: 0 },
      allowRob: merchantData.allowRob || false,
      angryFace: merchantData.angryFace || null,
      phrases: merchantData.phrases || [],
    });
    const refreshCurrentOpponentHp = () => {
      if (ghostData.value) {
        currentOpponentHp.value = Math.max(0, getEntityMaxHp(ghostData.value) - (ghostData.value.damageTaken || 0));
        return;
      }
      if (merchantData.value) {
        currentOpponentHp.value = Math.max(0, getEntityMaxHp(merchantData.value) - (merchantData.value.damageTaken || 0));
        return;
      }
      currentOpponentHp.value = 0;
    };
    const startCountdown = () => {
      if (expiresAt.value && !timer) {
        timeLeft.value = expiresAt.value - Date.now();
        if (timeLeft.value > 0) {
          timer = setInterval(() => {
            timeLeft.value = expiresAt.value - Date.now();
            if (timeLeft.value <= 0) {
              clearInterval(timer);
              timer = null;
              if (!fightResult.value) {
                fightResult.value = 'หมดเวลา! ผู้เล่นตายทันที!';
                stopAutoAttack('อีเวนต์หมดเวลา');
              }
            }
          }, 1000);
        } else {
          timeLeft.value = 0;
          if (!fightResult.value) {
            fightResult.value = 'หมดเวลา! ผู้เล่นตายทันที!';
            stopAutoAttack('อีเวนต์หมดเวลา');
          }
        }
      }
    };
    const stopCountdown = () => {
      if (timer) { clearInterval(timer); timer = null; }
    };
    const closeModal = () => {
      isClosed.value = true;
      fightResult.value = 'หน้าต่างถูกปิด';
      stopAutoAttack('หน้าต่างถูกปิด');
      emit('close');
    };

    // ===== Data Fetchers =====
    const fetchRewards = async (rewardItemIds, embeddedRewardMap = rewardDataMap.value) => {
      try {
        rewards.value = [];
        const rewardMap = embeddedRewardMap || {};
        if (eventData.value.diamondReward > 0) {
          rewards.value.push({
            id: 'diamond', name: 'เพชร',
            img: 'https://firebasestorage.googleapis.com/v0/b/zombiekub.firebasestorage.app/o/item2%2F%E0%B8%97%E0%B8%B1%E0%B9%88%E0%B8%A7%E0%B9%84%E0%B8%9B%2F%E0%B9%80%E0%B8%9E%E0%B8%8A%E0%B8%A3.png?alt=media&token=d0f95851-b518-46cd-aa78-4729cc6d9f41',
            amount: eventData.value.diamondReward, weight: 0, rarity: 10,
            rarityColors: rarityColors.value(10), transfer: false,
          });
        }
        if (true) {
          for (const rewardId of rewardItemIds || []) {
            const rewardData = rewardMap[rewardId];
            if (!rewardData) {
              continue;
            }
            rewards.value.push(buildRewardEntry(rewardId, rewardData));
          }
        }
        selectedRewardIds.value = [];
      } catch (error) {
        fightResult.value = 'เกิดข้อผิดพลาดในการโหลดรางวัล';
      }
    };
    const fetchSituationRewards = async (situationsData) => {
      const updatedSituations = [];
      for (const situation of (situationsData || [])) {
        const updatedRewards = [];
        if (situation.rewards?.length) {
          for (const reward of situation.rewards) {
            if (reward.img || reward.imageUrl || reward.rarity || reward.name) {
              updatedRewards.push({
                ...reward,
                name: reward.name || reward.itemName,
                img: reward.img || reward.imageUrl || 'default-item.png',
                rarityColors: rarityColors.value(reward.rarity || 1),
              });
            } else {
              const itemPreview = await getGameItemPreview(reward.itemName);
              updatedRewards.push({ ...reward, name: itemPreview.name, img: itemPreview.img, rarityColors: itemPreview.rarityColors });
            }
          }
        }
        updatedSituations.push({ ...situation, rewards: updatedRewards });
      }
      return updatedSituations;
    };
    const fetchBossGhostDrops = async (drops) => {
      const updatedDrops = [];
      for (const drop of (drops || [])) {
        const dropName = drop?.name || drop?.itemName || drop;
        if (!dropName) {
          continue;
        }
        if (drop.img || drop.imageUrl || drop.rarity || drop.name) {
          updatedDrops.push({
            ...drop,
            name: drop.name || drop.itemName,
            img: drop.img || drop.imageUrl || 'default-item.png',
            rarityColors: rarityColors.value(drop.rarity || 1),
          });
        } else {
          const itemPreview = await getGameItemPreview(dropName);
          updatedDrops.push({
            ...(typeof drop === 'string' ? { itemName: dropName } : drop),
            name: itemPreview.name,
            img: itemPreview.img,
            rarityColors: itemPreview.rarityColors,
          });
        }
      }
      return updatedDrops;
    };
    const subscribeMerchants = async (merchantIds, embeddedMerchantMap = null) => {
      unsubscribeMerchants.value.forEach((unsub) => unsub());
      unsubscribeMerchants.value = [];
      merchants.value = [];
      const merchantMap = embeddedMerchantMap || eventData.value.merchantDataMap || {};
      const nextMerchants = [];
      for (const id of (merchantIds || [])) {
        const merchant = toMerchantPreview(id, merchantMap[id]);
        if (merchant) {
          nextMerchants.push(merchant);
        }
      }
      merchants.value = nextMerchants;
      selectedMerchantId.value = pickActiveId(selectedMerchantId.value, nextMerchants);
    };
    const subscribeBossGhosts = async (bossGhostIds, embeddedBossGhostMap = null) => {
      unsubscribeBossGhosts.value.forEach((unsub) => unsub());
      unsubscribeBossGhosts.value = [];
      bossGhosts.value = [];
      const bossGhostMap = embeddedBossGhostMap || eventData.value.bossGhostDataMap || {};
      const nextBossGhosts = [];
      for (const id of (bossGhostIds || [])) {
        const data = bossGhostMap[id];
        if (!data) {
          continue;
        }
        const drops = await fetchBossGhostDrops(data.drops || data.items);
        nextBossGhosts.push({
          id,
          ...data,
          imageUrl: data.imageUrl || data.img || 'default-ghost.png',
          level: data.level || 'N/A',
          stats: data.stats || {},
          drops,
        });
      }
      bossGhosts.value = nextBossGhosts;
      selectedBossGhostId.value = pickActiveId(selectedBossGhostId.value, nextBossGhosts);
    };

    // ===== UI Actions =====
    const toggleRewardSelection = (rewardId) => {
      const reward = rewards.value.find(r => r.id === rewardId);
      const newWeight = selectedRewardIds.value.includes(rewardId) ? selectedWeight.value : selectedWeight.value + (reward.weight || 0);
      if (newWeight > capacity.value) { fightResult.value = 'น้ำหนักเกิน! ไม่สามารถเลือกไอเทมเพิ่มได้'; return; }
      if (selectedRewardIds.value.includes(rewardId)) { selectedRewardIds.value = selectedRewardIds.value.filter(id => id !== rewardId); }
      else { selectedRewardIds.value = [...selectedRewardIds.value, rewardId]; }
      fightResult.value = '';
    };
    const selectAllRewards = () => {
      if (!rewards.value.length) { fightResult.value = 'ไม่มีไอเทมให้เลือก!'; return; }
      let totalWeight = 0;
      const selectableRewards = rewards.value.filter(r => { totalWeight += r.weight || 0; return totalWeight <= capacity.value; });
      if (!selectableRewards.length) { fightResult.value = 'น้ำหนักเกิน!'; return; }
      selectedRewardIds.value = selectableRewards.map(r => r.id);
      fightResult.value = `เลือก ${selectableRewards.length} ไอเทมเรียบร้อย`;
    };
    const selectSituation = (situationName) => { selectedSituationName.value = situationName; fightResult.value = `เลือกสถานการณ์ "${situationName}"`; };
    const toggleAllSelections = () => {
      if (selectedRewardIds.value.length) { selectedRewardIds.value = []; fightResult.value = 'ยกเลิกการเลือกทั้งหมดแล้ว'; }
      else { selectAllRewards(); }
    };
    const toggleAutoAttack = (intervalMs) => {
      if (isAutoAttacking.value) { stopAutoAttack('ผู้เล่นปิด Auto Attack'); }
      else { startAutoAttack(intervalMs); fightResult.value = `Auto Attack ถูกเปิด (${intervalMs}ms)`; }
    };
    const startAutoAttack = (intervalMs) => {
  if (isAutoAttacking.value) return;
  isAutoAttacking.value = true;
  autoAttackIntervalMs.value = intervalMs;
  noGhostSince = null;

  if (autoAttackInterval) {
    clearTimeout(autoAttackInterval);
    autoAttackInterval = null;
  }

  const runAutoAttack = async () => {
    if (!isAutoAttacking.value) return;

    try {
      if (opponentType.value === 'ghost' && (energyLevel.value || 0) <= 0) {
        stopAutoAttack('พลังงานหมด: Auto Attack หยุด');
        return;
      }

      if (!opponentData.value) {
        if (noGhostSince == null) noGhostSince = Date.now();

        if (Date.now() - noGhostSince >= NO_GHOST_GRACE_MS) {
          stopAutoAttack('ไม่มีศัตรูต่อ: Auto Attack หยุด');
          return;
        }
      } else {
        noGhostSince = null;

        if (!isFighting.value) {
          try {
            await attackOpponentHandler();
          } catch (e) {
            const msg = (e?.message || '').toString();
            if (
              (msg.includes('พลังงานไม่เพียงพอ') ||
                (e?.code === 'failed-precondition' && energyLevel.value <= 0)) &&
              opponentType.value === 'ghost'
            ) {
              stopAutoAttack('พลังงานหมด: Auto Attack หยุด');
              return;
            }
          }
        }
      }
    } finally {
      if (isAutoAttacking.value) {
        autoAttackInterval = setTimeout(runAutoAttack, intervalMs);
      }
    }
  };

  autoAttackInterval = setTimeout(runAutoAttack, intervalMs);
};
    const stopAutoAttack = (reason = '') => {
      if (autoAttackInterval) { clearTimeout(autoAttackInterval); autoAttackInterval = null; }
      isAutoAttacking.value = false;
      autoAttackIntervalMs.value = null;
      if (reason) fightResult.value = reason;
    };
    const attackOpponentHandler = async () => {
      if (isFighting.value || (opponentType.value === 'ghost' && energyLevel.value <= 0)) return;
      isFighting.value = true; fightResult.value = 'กำลังโจมตี...';
      try {
        let response = null;
        if (opponentType.value === 'ghost') {
          response = await ghostActions({ action: 'ghostActions', subAction: 'attackGhost', eventId: props.eventId });
          fightResult.value = response.data.message;
        } else if (opponentType.value === 'merchant') {
          response = await gameActions({ action: 'merchantActions', subAction: 'attackMerchant', eventId: props.eventId });
          fightResult.value = response.data.message;
        }
        if (response?.data?.completed) { stopAutoAttack('ไม่มีเป้าหมายให้โจมตีต่อ'); }
        if (fightResult.value.includes('ผู้เล่นตาย')) { stopCountdown(); stopAutoAttack('ผู้เล่นตาย'); }
      } catch (err) {
        fightResult.value = err.message || 'เกิดข้อผิดพลาดในการโจมตี';
        const msg = (err?.message || '').toString();
        if (msg.includes('ไม่มีเป้าหมาย') || msg.includes('ไม่มีการต่อสู้') || msg.includes('ตายแล้ว')) {
          stopAutoAttack('ไม่มีเป้าหมายให้โจมตีต่อ');
        }
      }
      finally { isFighting.value = false; }
    };
    const fleeOpponentHandler = async () => {
      if (isFighting.value) return;
      isFighting.value = true; fightResult.value = 'กำลังหนี...';
      try {
        if (opponentType.value === 'ghost') {
          const response = await ghostActions({ action: 'ghostActions', subAction: 'fleeFight', eventId: props.eventId });
          fightResult.value = response.data.message;
          if (response.data.completed) { stopCountdown(); stopAutoAttack('หลบหนีสำเร็จ'); showFleeOptions.value = false; }
        } else if (opponentType.value === 'merchant') {
          const response = await gameActions({ action: 'merchantActions', subAction: 'fleeMerchant', eventId: props.eventId });
          fightResult.value = response.data.message;
          if (response.data.completed) { stopCountdown(); stopAutoAttack('หลบหนีสำเร็จ'); showFleeOptions.value = false; }
        }
      } catch (err) { fightResult.value = err.message || 'เกิดข้อผิดพลาดในการหนี'; }
      finally { isFighting.value = false; }
    };
    const fleeBySilver = async () => {
      if (isFighting.value) return;
      isFighting.value = true; fightResult.value = 'กำลังหนีด้วยเหรียญเงิน...';
      try {
        const response = await ghostActions({ action: 'ghostActions', subAction: 'fleeBySilver', eventId: props.eventId });
        fightResult.value = response.data.message;
        if (response.data.completed) { stopCountdown(); stopAutoAttack('หลบหนีสำเร็จ'); showFleeOptions.value = false; }
      } catch (err) { fightResult.value = err.message || 'เกิดข้อผิดพลาดในการหนีด้วยเหรียญเงิน'; }
      finally { isFighting.value = false; }
    };
    const handleBuyClick = (merchantId, itemName) => {
      if (isFighting.value) return;
      if (isConfirmingBuy.value && selectedItemToBuy.value === itemName) {
        buyItem(merchantId, itemName);
        isConfirmingBuy.value = false; selectedItemToBuy.value = null;
        if (confirmBuyTimeout) { clearTimeout(confirmBuyTimeout); confirmBuyTimeout = null; }
      } else {
        isConfirmingBuy.value = true; selectedItemToBuy.value = itemName; fightResult.value = 'คลิกอีกครั้งเพื่อยืนยันการซื้อ';
        confirmBuyTimeout = setTimeout(() => { isConfirmingBuy.value = false; selectedItemToBuy.value = null; fightResult.value = ''; }, 5000);
      }
    };
    const buyItem = async (merchantId, itemName) => {
      if (isFighting.value) return;
      isFighting.value = true; fightResult.value = 'กำลังซื้อ...';
      try {
        const response = await gameActions({ action: 'merchantActions', subAction: 'buyFromMerchant', eventId: props.eventId, merchantId, itemName });
        fightResult.value = response.data.message; selectedItemName.value = null;
      } catch (err) { fightResult.value = err.message || 'เกิดข้อผิดพลาดในการซื้อ'; }
      finally { isFighting.value = false; }
    };
    const robMerchant = async (merchantId) => {
      if (isFighting.value) return;
      isFighting.value = true; fightResult.value = 'กำลังปล้น...';
      try {
        const response = await gameActions({ action: 'merchantActions', subAction: 'robMerchant', eventId: props.eventId, merchantId });
        fightResult.value = response.data.message;
      } catch (err) { fightResult.value = err.message || 'เกิดข้อผิดพลาดในการปล้น'; }
      finally { isFighting.value = false; }
    };
    const handleGoHomeClick = () => {
      if (isFighting.value) return;
      if (isConfirmingGoHome.value) {
        goHome(); isConfirmingGoHome.value = false;
        if (confirmTimeout) { clearTimeout(confirmTimeout); confirmTimeout = null; }
      } else {
        isConfirmingGoHome.value = true; fightResult.value = 'คลิกปุ่มอีกครั้งเพื่อยืนยันการกลับบ้าน';
        confirmTimeout = setTimeout(() => { isConfirmingGoHome.value = false; fightResult.value = ''; }, 2000);
      }
    };
    const handleContinueExploringClick = () => {
      if (isFighting.value) return;
      if (isConfirmingExplore.value) {
        continueExploring(); isConfirmingExplore.value = false;
        if (confirmExploreTimeout) { clearTimeout(confirmExploreTimeout); confirmExploreTimeout = null; }
      } else {
        isConfirmingExplore.value = true; fightResult.value = 'คลิกปุ่มอีกครั้งเพื่อยืนยันการสำรวจต่อ';
        confirmExploreTimeout = setTimeout(() => { isConfirmingExplore.value = false; fightResult.value = ''; }, 2000);
      }
    };
    const goHome = async () => {
      if (isFighting.value) return;
      isFighting.value = true; fightResult.value = 'กำลังกลับบ้าน...';
      try {
        const response = await gameActions({ action: 'goHome', eventId: props.eventId });
        fightResult.value = response.data.message;
        if (response.data?.safe) {
          stopCountdown();
          selectedRewardIds.value = [];
          showFleeOptions.value = false;
          stopAutoAttack('กลับบ้านแล้ว');
          return;
        }
        fightResult.value = response.data.message; stopCountdown(); stopAutoAttack('กลับบ้านแล้ว');
      } catch (err) { fightResult.value = err.message || 'เกิดข้อผิดพลาดในการกลับบ้าน'; }
      finally { isFighting.value = false; }
    };
    const goHomeHandler = async () => {
      if (isFighting.value) return;
      isFighting.value = true;
      fightResult.value = 'กำลังกลับบ้าน...';
      try {
        const response = await gameActions({ action: 'goHome', eventId: props.eventId });
        fightResult.value = response.data?.message || '';
        if (response.data?.safe) {
          stopCountdown();
          selectedRewardIds.value = [];
          showFleeOptions.value = false;
          stopAutoAttack('กลับบ้านแล้ว');
          return;
        }
        stopAutoAttack();
      } catch (err) {
        fightResult.value = err.message || 'เกิดข้อผิดพลาดในการกลับบ้าน';
      } finally {
        isFighting.value = false;
      }
    };
    const addToBag = async () => {
  if (isAddingToBag.value || !selectedRewardIds.value.length || !hasReward.value) return;

  isAddingToBag.value = true;
  fightResult.value = 'กำลังนำใส่กระเป๋า.';

  try {
    const selectedIds = [...selectedRewardIds.value];

    if (!selectedIds.length) {
      fightResult.value = 'กรุณาเลือกรางวัลก่อน';
      return;
    }

    const payload = {
      eventId: props.eventId,
      rewardItemIds: selectedIds
    };

    const response = await gameActions({
      action: 'addToBag100',
      ...payload
    });

    selectedRewardIds.value = [];
    fightResult.value = response.data?.message || 'นำรางวัลใส่กระเป๋าเรียบร้อย';

    lockGoHomeTemporarily();

    if (response.data?.eventFinished) {
      stopCountdown();
      stopAutoAttack();
      showFleeOptions.value = false;
    }

    return;
  } catch (err) {
    fightResult.value = err.message || 'เกิดข้อผิดพลาดในการนำใส่กระเป๋า';
  } finally {
    isAddingToBag.value = false;
  }
};
    const continueExploringSituation = async () => {
      if (isFighting.value || !hasSituation.value || !selectedSituationName.value) return;
      isFighting.value = true; fightResult.value = `กำลังสำรวจสถานการณ์ "${selectedSituationName.value}"...`;
      try {
        const response = await exploreSupernatural({ action: 'exploreSupernatural', subAction: 'continueExploringSituation', eventId: props.eventId, situationName: selectedSituationName.value });
        lockGoHomeTemporarily();
        fightResult.value = response.data.message; hasSituation.value = false; situations.value = []; selectedSituationName.value = null; activeTab.value = 'rewards';
      } catch (err) { fightResult.value = err.message || 'เกิดข้อผิดพลาดในการสำรวจสถานการณ์'; }
      finally { isFighting.value = false; }
    };
    const continueExploring = async () => {
      if (isFighting.value || opponentData.value) return;
      isFighting.value = true; fightResult.value = 'กำลังสำรวจต่อ...';
      try {
        const response = await exploreSupernatural({ action: 'exploreSupernatural', subAction: 'continueExploring2', eventId: props.eventId });
        lockGoHomeTemporarily();
        fightResult.value = response.data.message;
        rewards.value = []; selectedRewardIds.value = []; situations.value = []; selectedSituationName.value = null;
        merchants.value = []; selectedMerchantId.value = null; bossGhosts.value = []; selectedBossGhostId.value = null; activeTab.value = 'rewards';
      } catch (err) { fightResult.value = err.message || 'เกิดข้อผิดพลาดในการสำรวจต่อ'; }
      finally { isFighting.value = false; }
    };
    const handleRobClick = (merchantId) => {
      if (isFighting.value) return;
      if (isConfirmingRob.value) {
        robMerchant(merchantId); isConfirmingRob.value = false;
        if (confirmRobTimeout) { clearTimeout(confirmRobTimeout); confirmRobTimeout = null; }
      } else {
        isConfirmingRob.value = true; fightResult.value = 'คลิกอีกครั้งเพื่อยืนยันการปล้น';
        confirmRobTimeout = setTimeout(() => { isConfirmingRob.value = false; fightResult.value = ''; }, 5000);
      }
    };
    const selectBossGhost = (bossGhostId) => { selectedBossGhostId.value = bossGhostId; };
    const handleFightBossClick = (bossId) => {
      if (isFighting.value) return;
      
      if (confirmingBossId.value === bossId) {
        fightBossGhost(bossId); confirmingBossId.value = null;
        if (confirmBossTimeout) { clearTimeout(confirmBossTimeout); confirmBossTimeout = null; }
      } else {
        confirmingBossId.value = bossId; fightResult.value = 'คลิกอีกครั้งเพื่อยืนยันการสู้กับบอส';
        confirmBossTimeout = setTimeout(() => { confirmingBossId.value = null; fightResult.value = ''; }, 5000);
      }
    };
    const fightBossGhost = async (bossGhostId) => {
      if (isFighting.value) return;
      isFighting.value = true; fightResult.value = 'กำลังเริ่มต่อสู้กับบอส...';
      try {
        const response = await ghostActions({ action: 'ghostActions', subAction: 'selectBossGhost', eventId: props.eventId, bossGhostId });
        lockGoHomeTemporarily();
        fightResult.value = response.data.message;
        bossGhosts.value = bossGhosts.value.filter(b => b.id !== bossGhostId); selectedBossGhostId.value = null;
      } catch (err) { fightResult.value = err.message || 'เกิดข้อผิดพลาดในการเริ่มต่อสู้กับบอส'; }
      finally { isFighting.value = false; }
    };

    // ===== Watchers =====
    watch(selectedMerchant, (newMerchant) => {
      selectedItemName.value = newMerchant?.items?.length > 0 ? newMerchant.items[0].name : null;
    });
    watch([hasReward, rewardItemIds, rewardDataMap, () => eventData.value.diamondReward], async ([newHas, newIds, newRewardMap]) => {
      if (newHas && (newIds?.length > 0 || Number(eventData.value.diamondReward || 0) > 0)) {
        await fetchRewards(newIds, newRewardMap);
      }
      else if (!newHas) { rewards.value = []; selectedRewardIds.value = []; }
    }, { immediate: true, deep: true });
   watch(hasReward, (newVal, oldVal) => {
  if (newVal && !oldVal) {
    lockGoHomeTemporarily(2000);
  } else if (!newVal) {
    syncGoHomeAvailability();
  }

  if (newVal && isAutoAttacking.value) {
    noGhostSince = Date.now();
    fightResult.value = 'มีรางวัลให้เก็บ ระบบ Auto Attack จะรอเป้าหมายถัดไป';
  }
});
    watch(hasReward, (newVal, oldVal) => {
      if (newVal && !oldVal && !isAutoAttacking.value && opponentData.value === null) {
        fightResult.value = 'มีรางวัลให้เก็บ: Auto Attack หยุด';
      }
    });
    watch(opponentData, (newVal, oldVal) => {
      if (oldVal && !newVal) { isActionsDisabled.value = true; setTimeout(() => { isActionsDisabled.value = false; }, 2000); }
    });
    watch(hasSituation, (newVal, oldVal) => {
      if (oldVal && !newVal) { isActionsDisabled.value = true; setTimeout(() => { isActionsDisabled.value = false; }, 2000); }
    });
    watch(hasBossGhost, (newVal, oldVal) => {
      if (oldVal && !newVal) { isActionsDisabled.value = true; setTimeout(() => { isActionsDisabled.value = false; }, 2000); }
    });
    watch(() => props.eventId, (newEventId, oldEventId) => {
      if (newEventId === oldEventId) return;
      stopCountdown();
      stopAutoAttack();
      if (goHomeUnlockTimeout) {
        clearTimeout(goHomeUnlockTimeout);
        goHomeUnlockTimeout = null;
      }
      subscribeToData();
    });

    // ===== Firestore Subscription =====
    const subscribeToData = async () => {
      try {
        resetEmbeddedEventSubscriptions();
        if (!props.eventId) {
          resetEmbeddedEventState();
          stopCountdown();
          isLoading.value = false;
          return;
        }
        isLoading.value = true;
        unsubscribeEvent = projectFirestore.collection('events').doc(props.eventId)
          .onSnapshot(async (doc) => {
            if (doc.exists) {
              const data = doc.data();
              eventData.value = {
                ...data,
                escapeCoins: data.escapeCoins || 0,
                escapeAttempts: data.escapeAttempts || 0,
                diamondReward: data.diamondReward || 0,
              };
              hasReward.value = Boolean(data.hasReward || (data.rewardItemIds?.length > 0) || (data.diamondReward || 0) > 0);
              rewardItemIds.value = data.rewardItemIds || [];
              rewardDataMap.value = data.rewardDataMap || {};
              hasSituation.value = data.hasSituation || false;
              hasMerchant.value = Boolean(data.hasMerchant || data.merchantIds?.length > 0 || data.currentMerchantId);
              hasBossGhost.value = Boolean(data.hasBossGhost || data.bossGhostIds?.length > 0 || data.currentBossGhostId);
              ghostKilled.value = data.ghostKilled || 0;
              currentMerchantId.value = data.currentMerchantId || null;

              // ✅ Subscribe boss bar เมื่อรู้ mapName จาก event
              const newMapName = data.mapName || '';
              if (newMapName && newMapName !== mapName.value) {
                mapName.value = newMapName;
                // ดึง bossGhosts list จาก maps collection
                projectFirestore.collection('maps').doc(newMapName).get().then(mapDoc => {
                  if (mapDoc.exists) {
                    const bossGhostList = (mapDoc.data().bossGhosts || []).map(b => typeof b === 'string' ? b : b?.name);
                    subscribeMapBossStatuses(newMapName, bossGhostList.filter(Boolean));
                  }
                });
              } else if (!mapName.value) {
                mapName.value = newMapName;
              }

              if (data.situations?.length) { situations.value = await fetchSituationRewards(data.situations); }
              else { situations.value = []; }
              const newExpiresAt = data.expiresAt?.toDate().getTime() || null;
              if (newExpiresAt !== expiresAt.value) { expiresAt.value = newExpiresAt; stopCountdown(); if (expiresAt.value) startCountdown(); }
              else if (expiresAt.value && !timer) { startCountdown(); }

              if (data.currentGhostId || data.currentBossGhostId) {
                const ghostId = data.currentGhostId || data.currentBossGhostId;
                const embeddedGhost = data.currentBossGhostId
                  ? data.bossGhostDataMap?.[ghostId]
                  : data.ghostDataMap?.[ghostId];
                if (embeddedGhost) {
                  if (unsubscribeGhost) {
                    unsubscribeGhost();
                    unsubscribeGhost = null;
                  }
                  ghostData.value = { id: ghostId, ...embeddedGhost };
                } else {
                  ghostData.value = null;
                  currentOpponentHp.value = 0;
                }
              } else {
                if (unsubscribeGhost) { unsubscribeGhost(); unsubscribeGhost = null; }
                ghostData.value = null; currentOpponentHp.value = 0;
              }
              if (data.currentMerchantId) {
                const embeddedMerchant = data.merchantDataMap?.[data.currentMerchantId];
                if (embeddedMerchant) {
                  if (unsubscribeMerchant) {
                    unsubscribeMerchant();
                    unsubscribeMerchant = null;
                  }
                  merchantData.value = { id: data.currentMerchantId, ...embeddedMerchant };
                } else {
                  merchantData.value = null;
                  currentOpponentHp.value = 0;
                }
              } else {
                if (unsubscribeMerchant) { unsubscribeMerchant(); unsubscribeMerchant = null; }
                merchantData.value = null; currentOpponentHp.value = 0;
              }
              refreshCurrentOpponentHp();
              if (data.currentGhostId || data.currentBossGhostId || data.currentMerchantId) {
                if (unsubscribeBossGhosts.value.length > 0) { unsubscribeBossGhosts.value.forEach(u => u()); unsubscribeBossGhosts.value = []; bossGhosts.value = []; }
                if (unsubscribeMerchants.value.length > 0) { unsubscribeMerchants.value.forEach(u => u()); unsubscribeMerchants.value = []; merchants.value = []; }
              } else {
                showFleeOptions.value = false;
                if (hasMerchant.value) {
                  await subscribeMerchants(data.merchantIds, data.merchantDataMap);
                } else {
                  unsubscribeMerchants.value.forEach(u => u());
                  unsubscribeMerchants.value = [];
                  merchants.value = [];
                  selectedMerchantId.value = null;
                }
                if (hasBossGhost.value) {
                  await subscribeBossGhosts(data.bossGhostIds, data.bossGhostDataMap);
                } else {
                  unsubscribeBossGhosts.value.forEach(u => u());
                  unsubscribeBossGhosts.value = [];
                  bossGhosts.value = [];
                  selectedBossGhostId.value = null;
                }
                const validTabs = [];
                if (hasReward.value) validTabs.push('rewards');
                if (hasSituation.value) validTabs.push('situations');
                if (hasMerchant.value) validTabs.push('merchants');
                if (hasBossGhost.value) validTabs.push('bossGhosts');
                if (!validTabs.includes(activeTab.value)) {
                  activeTab.value = validTabs[0] || 'rewards';
                }
              }
            } else {
              resetEmbeddedEventState();
              stopCountdown();
            }
            isLoading.value = false;
          });
        // (character/energy data now comes from usePlayerStore)
      } catch (_err) { fightResult.value = 'ไม่สามารถโหลดข้อมูลได้'; isLoading.value = false; }
    };

    // ===== Lifecycle =====
    onMounted(() => { subscribeToData(); });
    onUnmounted(() => {
      stopCountdown(); stopAutoAttack();
      unsubscribeMapBossStatuses(); // ✅
      if (confirmTimeout) clearTimeout(confirmTimeout);
      if (confirmRobTimeout) clearTimeout(confirmRobTimeout);
      if (confirmExploreTimeout) clearTimeout(confirmExploreTimeout);
      if (confirmBossTimeout) clearTimeout(confirmBossTimeout);
      if (confirmBuyTimeout) clearTimeout(confirmBuyTimeout);
      if (goHomeUnlockTimeout) clearTimeout(goHomeUnlockTimeout);
      resetEmbeddedEventSubscriptions();
      if (unsubscribeEvent) unsubscribeEvent();
      if (unsubscribeGhost) unsubscribeGhost();
      if (unsubscribeMerchant) unsubscribeMerchant();
      unsubscribeMerchants.value.forEach(u => u());
      unsubscribeBossGhosts.value.forEach(u => u());
    });

    return {
      isClosed, closeModal, capacity, toggleAllSelections, mapName, isLoading, isLoadingRewards,
      ghostKilled, opponentData, opponentType, opponentStats, currentOpponentHp, characterData,
      characterPrivateData, calculatedStats, currentHealth, energyLevel, maxEnergy, fightResult,
      isFighting, hasReward, hasSituation, hasMerchant, hasBossGhost, situations, selectedSituationName,
      rewards, selectedRewardIds, merchants, selectedMerchantId, selectedItemName, timeLeft, fleeChance,
      formatTime, handleImageError, attackOpponentHandler, toggleAutoAttack, fleeOpponentHandler,
      fleeBySilver, handleGoHomeClick, goHome: goHomeHandler, toggleRewardSelection, selectAllRewards, addToBag,
      isAddingToBag, continueExploringSituation, handleContinueExploringClick, selectSituation,
      buyItem, robMerchant, currentWeight, maxLoad, selectedWeight, isAutoAttacking, autoAttackIntervalMs,
      eventData, showFleeOptions, isConfirmingGoHome, activeTab, selectedMerchant, merchantStats,
      isConfirmingRob, handleRobClick, canGoHome, randomPhrase, isDanger, bossGhosts,
      selectedBossGhostId, selectBossGhost, handleFightBossClick, isConfirmingExplore, bossStats,
      confirmingBossId, isConfirmingBuy, selectedItemToBuy, handleBuyClick, isActionsDisabled,
      mapBossStatuses, // ✅
      sortedMapBossStatuses, // ✅
    };
  },
};
</script>

<style scoped>
.scrollbar-thin::-webkit-scrollbar { width: 5px; }
.scrollbar-thin::-webkit-scrollbar-track { background: #2d3748; border-radius: 10px; }
.scrollbar-thin::-webkit-scrollbar-thumb { background: #4a5568; border-radius: 10px; }
.scrollbar-thin::-webkit-scrollbar-thumb:hover { background: #6b7280; }
.scrollbar-thin { scrollbar-width: thin; scrollbar-color: #4a5568 #2d3748; }
@keyframes modalFade { from { opacity: 0; } to { opacity: 1; } }
.animate-modalFade { animation: modalFade 0.3s ease-out; }
@keyframes scaleIn { from { transform: scale(0.95); opacity: 0; } to { transform: scale(1); opacity: 1; } }
.animate-scaleIn { animation: scaleIn 0.3s ease-out; }
@keyframes pulse { 0% { transform: scale(1); } 50% { transform: scale(1.2); } 100% { transform: scale(1); } }
.animate-pulse { animation: pulse 1.5s infinite; }
@keyframes bounce { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-4px); } }
.animate-bounce { animation: bounce 0.5s infinite; }
@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
.animate-spin { animation: spin 1s linear infinite; }
@keyframes spin-pulse { 0% { transform: rotate(0deg) scale(1); } 50% { transform: rotate(180deg) scale(1.2); } 100% { transform: rotate(360deg) scale(1); } }
.animate-spin-pulse { animation: spin-pulse 1.2s ease-in-out infinite; }
button:disabled { opacity: 0.6; cursor: not-allowed; }
@media (max-width: 640px) {
  .text-sm { font-size: 0.75rem; }
  .text-xs { font-size: 0.625rem; }
  .p-4 { padding: 0.75rem; }
  .w-16 { width: 3rem; height: 3rem; }
  .max-h-64 { max-height: 16rem; }
}
.fade-list-enter-active, .fade-list-leave-active { transition: opacity 200ms ease, transform 200ms ease; }
.fade-list-enter-from, .fade-list-leave-to { opacity: 0; transform: translateY(4px); }
.text-xxs { font-size: 10px; line-height: 1rem; }
</style>
